import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './RecentToolsCard.module.css';
import TruncatedText from '../../../ui/TruncatedText/TruncatedText';
import { Plus } from 'lucide-react';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';
import { User } from '../../../types/user';
import { McpTool } from '../../../types/types';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { getTypeMetaByConnectionConfigId } from '../../../utils/utils';
import { Environment } from '../../../types/environment';
import { getToolValidationError } from '../../../lib/toolCompletion';

export default function RecentToolsCard({ tools, user }: { tools: McpTool[]; user: User | null }) {
    const connectors = useConnectionsStore(state => state.connectors);
    // Compute recent tools immediately, so they show up on first render
    const computeRecentTools = (tools: McpTool[]) => {
        return Array.isArray(tools) && tools.length > 0
            ? tools
                .filter(
                    tool =>
                        tool.mainConnectionId
                )
                .slice(-3)
                .reverse()
            : [];
    };

    const [recentTools, setRecentTools] = useState<McpTool[]>(() => computeRecentTools(tools));

    useEffect(() => {
        setRecentTools(computeRecentTools(tools));
    }, [tools]);

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <h2>Recent Tools</h2>
                <Link to="/toolslist" className={styles.link}>View All</Link>
            </div>
            {recentTools.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No tools created yet.</p>
                    {user && hasMinRole(user?.role?.id, UserRole.Admin) && (
                        <Link to="/toolslist/new" className={styles.iconButton}>
                            <Plus size={16} />
                            <span>Create Your First Tool</span>
                        </Link>
                    )}
                </div>
            ) : (
                <div className={styles.toolsList}>
                    {recentTools.map((tool) => {
                        const con = connectors.find(p => p.id === tool.mainConnectionId);
                        const isConnectorActive = !!(con && con.isActive);
                        const isToolDisabledByConnector = !isConnectorActive;
                        const env = con?.environment as Environment | undefined;
                        const isMssql = con?.connectionTypeId === 7;

                        return (
                            <div key={tool.id} className={styles.toolItem}>
                                <div className={styles.toolInfo}>
                                    <Link to={`/toolslist/${tool.id}`} state={{ from: '/dashboard' }} className={styles.toolLink}>
                                        <TruncatedText
                                            className={styles.toolName}
                                            text={tool.name}
                                            maxLength={
                                                window.innerWidth < 500 ? 10 : window.innerWidth < 1500 ? 20 : 30
                                            }
                                            letReadMore={false}
                                            showFullOnHover={false}
                                        />
                                    </Link>
                                    <TruncatedText text={tool.description}
                                        maxLength={window.innerWidth < 500 ? 130 : window.innerWidth < 1500 ? 100 : 180} letReadMore={false} />
                                    <div className={styles.toolMeta}>
                                        {getToolValidationError(tool, isMssql) ? (
                                            <span
                                                className={`${styles.badge} ${styles.badgeError}`}
                                                title={getToolValidationError(tool, isMssql)!}
                                            >
                                                Error
                                            </span>
                                        ) : (
                                            <span className={`${styles.badge} ${tool.enabled ? styles.badgeSuccess : styles.badgeError}`}>
                                                {tool.enabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                        )}
                                        {(!con || isToolDisabledByConnector || !env) ? (
                                            <span className={`${styles.environmentBadge} ${styles.environmentInactive}`}>
                                                N/A
                                            </span>
                                        ) : (
                                            <span className={`${styles.environmentBadge} ${styles[`environment${env}`]}`}>
                                                {env === 'test' ? 'Test' : env === 'dev' ? 'Dev' : 'Prod'}
                                            </span>
                                        )}
                                        <span className={styles.method}>
                                            {(() => {
                                                const meta = getTypeMetaByConnectionConfigId(tool.mainConnectionId, connectors);
                                                return <img className={styles.icon} src={meta?.icon} />;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    );
}
